import { defineMessages } from 'react-intl';

export const codingDashboardMessages = defineMessages({
  title: {
    defaultMessage: 'Coding',
    id: 'miUkRYh0b+',
    description: 'Title used for the coding dashboard entry point and landing page',
  },
  allCasesTabLabel: {
    defaultMessage: 'All Cases',
    id: 'tajE7Y+6dU',
    description: 'Tab label for the coding dashboard showing every case',
  },
  unassignedCasesTabLabel: {
    defaultMessage: 'Unassigned',
    id: 'MqtuIMsTIq',
    description: 'Tab label for coding dashboard cases without an assignee',
  },
  myCasesTabLabel: {
    defaultMessage: 'My cases',
    id: 'V8WrS2uBMc',
    description: 'Tab label for coding dashboard cases assigned to the current user',
  },
  awaitingResponseTabLabel: {
    defaultMessage: 'Awaiting Response',
    id: 'qh5hDi/r+9',
    description: 'Tab label for coding dashboard cases awaiting a response',
  },
  pageTitle: {
    defaultMessage: 'Cases',
    id: 'JTz6JHnt+V',
    description: 'Heading for the coding dashboard page',
  },
  refreshLabel: {
    defaultMessage: 'Refresh',
    id: '5+1teP8Nk9',
    description: 'Accessible label for the coding dashboard refresh button',
  },
  searchPlaceholder: {
    defaultMessage: 'Search episode, patient, diagnosis, ward, or terminal digit',
    id: 'KL2vBJhbyx',
    description: 'Placeholder for the coding dashboard case search field',
  },
  searchLabel: {
    defaultMessage: 'Search cases',
    id: '8Wuj+dnE+r',
    description: 'Accessible label for the coding dashboard case search field',
  },
  clearFilters: {
    defaultMessage: 'Clear filters',
    id: 'xuQVFEP91U',
    description: 'Button label for clearing all coding dashboard filters',
  },
  statusFilterTitle: {
    defaultMessage: 'Status',
    id: 'YoyHuFxGLY',
    description: 'Title for the coding dashboard status filter',
  },
  readyStatusFilterOption: {
    defaultMessage: 'Ready',
    id: 'YspOC1Yxb/',
    description: 'Filter option for coding dashboard cases ready for coding',
  },
  inProgressStatusFilterOption: {
    defaultMessage: 'In progress',
    id: 'KXQ/cUatIP',
    description: 'Filter option for coding dashboard cases in progress',
  },
  caseGeneratingPrimaryDx: {
    defaultMessage: 'Case downloading, check back shortly',
    id: 'ZTTaqxDVsw',
    description:
      'Temporary primary diagnosis text shown while a coding case is still being generated',
  },
  caseGeneratingStatusLabel: {
    defaultMessage: 'Generating case',
    id: 'hvY9Mgj4lW',
    description:
      'Accessible label for the loading status indicator while a coding case is still being generated',
  },
  failedStatusFilterOption: {
    defaultMessage: 'Failed',
    id: 'bw2uexTEKd',
    description: 'Filter option for coding dashboard cases that failed processing',
  },
  modifiedStatusFilterOption: {
    defaultMessage: 'Modified',
    id: 'qJl1e+Qr9m',
    description: 'Filter option for coding dashboard cases modified after final review',
  },
  finalReviewStatusFilterOption: {
    defaultMessage: 'Final review',
    id: '00DvT/wNsR',
    description: 'Filter option for coding dashboard cases in final review',
  },
  completedStatusFilterOption: {
    defaultMessage: 'Completed',
    id: 'TaVcz136m3',
    description: 'Filter option for completed coding dashboard cases',
  },
  reopenedStatusFilterOption: {
    defaultMessage: 'Re-opened',
    id: 'lr5xa/xeus',
    description: 'Filter option for coding dashboard cases re-opened after completion',
  },
  assignedFilterTitle: {
    defaultMessage: 'Assigned',
    id: 'PTUQGix0MF',
    description: 'Title for the coding dashboard assignee filter',
  },
  unassignedFilterOption: {
    defaultMessage: 'Unassigned',
    id: '2H7PZiy7JS',
    description: 'Filter option for coding dashboard cases with no assignee',
  },
  loadingCasesAriaLabel: {
    defaultMessage: 'Loading coding cases',
    id: 'iV3KqY/3mI',
    description: 'Screen reader label announced while the coding dashboard is loading',
  },
  errorTitle: {
    defaultMessage: 'Unable to load coding cases',
    id: 'gsopjL1nTT',
    description: 'Error title for the coding dashboard query',
  },
  errorDescription: {
    defaultMessage: 'Please try again to load the coding queue.',
    id: 'Bmpk02m6pa',
    description: 'Error description for the coding dashboard query',
  },
  retry: {
    defaultMessage: 'Retry',
    id: 'iGo58LQBi+',
    description: 'Button label for retrying the coding dashboard query',
  },
  emptyCases: {
    defaultMessage: 'No coding cases found',
    id: '1PmDI6Kfqt',
    description: 'Empty state text for the coding dashboard table',
  },
  currentUserFallbackName: {
    defaultMessage: 'Me',
    id: '7R+vGu2icB',
    description: 'Fallback display name for the logged-in user',
  },
  assignToMe: {
    defaultMessage: 'Assign to me',
    id: 'h9GwV18k75',
    description: 'Action label for assigning coding dashboard cases to the current user',
  },
  unassignFromMe: {
    defaultMessage: 'Unassign from me',
    id: 'tVO1X7n5f9',
    description:
      'Action label for unassigning coding dashboard cases from the current user',
  },
  assignToCoder: {
    defaultMessage: 'Assign to {coderName}',
    id: '+EII6BnWuW',
    description: 'Toast title for assigning coding dashboard cases to another coder',
  },
  unassignFromCoder: {
    defaultMessage: 'Unassign from {coderName}',
    id: 'U4QFIu1BbM',
    description: 'Toast title for unassigning coding dashboard cases from another coder',
  },
  assignSelectedCasesToMeAriaLabel: {
    defaultMessage: 'Assign selected cases to me',
    id: 'kJLcopfogZ',
    description:
      'Accessible label for the bulk action that assigns selected coding dashboard cases',
  },
  assignSelectedCasesToCoderAriaLabel: {
    defaultMessage: 'Assign selected cases to another coder',
    id: 'FxoEbNwzQY',
    description:
      'Accessible label for the bulk action that assigns selected coding dashboard cases to another coder',
  },
  unassignSelectedCasesFromMeAriaLabel: {
    defaultMessage: 'Unassign selected cases from me',
    id: 'FHu+2UiJW7',
    description:
      'Accessible label for the bulk action that unassigns selected coding dashboard cases',
  },
  assignBulkAction: {
    defaultMessage: 'Assign',
    id: '89BkkuiEnA',
    description: 'Short bulk action label for assigning selected coding dashboard cases',
  },
  unassignBulkAction: {
    defaultMessage: 'Unassign',
    id: 'LtHsLqZM54',
    description:
      'Short bulk action label for unassigning selected coding dashboard cases',
  },
  exportGoldJsonlBulkAction: {
    defaultMessage: 'Export as JSONL',
    id: 'aoZAH1P0f4',
    description:
      'Bulk action label for exporting selected coding dashboard cases as gold.jsonl',
  },
  exportGoldJsonlBulkActionAriaLabel: {
    defaultMessage: 'Export selected cases as gold JSONL',
    id: 'wEuOxMi/QG',
    description:
      'Accessible label for exporting selected coding dashboard cases as gold.jsonl',
  },
  unassignedAssignedCellLabel: {
    defaultMessage: 'Unassigned',
    id: 'ZQY+RwRcCA',
    description: 'Assignee cell label for a coding dashboard case without an assignee',
  },
  assignPatientToMeAriaLabel: {
    defaultMessage: 'Assign {patientName} to me',
    id: 'Lm+VARDd12',
    description:
      'Accessible label for assigning one coding dashboard patient case to the current user',
  },
  assignPatientToCoderAriaLabel: {
    defaultMessage: 'Assign {patientName} to another coder',
    id: 'E9Zt8nBjzY',
    description:
      'Accessible label for assigning one coding dashboard patient case to another coder',
  },
  changePatientCoderAriaLabel: {
    defaultMessage: 'Change coder assigned to {patientName}',
    id: 'XcDaFPsg0i',
    description:
      'Accessible label for changing the coder assigned to one coding dashboard patient case',
  },
  unassignPatientFromMeAriaLabel: {
    defaultMessage: 'Unassign {patientName} from me',
    id: 'oloPMYvAFC',
    description:
      'Accessible label for unassigning one coding dashboard patient case from the current user',
  },
  assignCasesSuccessDescription: {
    defaultMessage: '{count} assigned',
    id: '4zudwrNRqW',
    description: 'Success toast body after assigning coding dashboard cases',
  },
  unassignCasesSuccessDescription: {
    defaultMessage: '{count} unassigned',
    id: 'mam0H3YE9E',
    description: 'Success toast body after unassigning coding dashboard cases',
  },
  assignCasesPartialSuccessDescription: {
    defaultMessage: '{updatedCount} assigned; {skippedCount} skipped',
    id: '/xQ6J2Tto1',
    description:
      'Partial success toast body after assigning coding dashboard cases when some cases were skipped',
  },
  unassignCasesPartialSuccessDescription: {
    defaultMessage: '{updatedCount} unassigned; {skippedCount} skipped',
    id: 'pyerTqS2DO',
    description:
      'Partial success toast body after unassigning coding dashboard cases when some cases were skipped',
  },
  assignmentErrorTitle: {
    defaultMessage: 'Unable to update assignment',
    id: 'ywcJQuT3lk',
    description: 'Error toast title when coding dashboard assignment update fails',
  },
  assignmentErrorDescription: {
    defaultMessage: 'Please try again.',
    id: 'Jyb8FAU6oE',
    description: 'Error toast body when coding dashboard assignment update fails',
  },
  createCaseButton: {
    defaultMessage: 'Create case',
    id: 'Ov9B/4KeNp',
    description: 'Button label that opens the self-serve create-case dialog',
  },
  createCaseDialogTitle: {
    defaultMessage: 'Create a case',
    id: '8ck8YJi2qy',
    description: 'Title of the self-serve create-case upload dialog',
  },
  createCaseDialogDescription: {
    defaultMessage: 'Upload one or more documents to create a demo coding case.',
    id: 'xpfM8kOUw1',
    description: 'Supporting copy in the self-serve create-case dialog',
  },
  createCaseDropzoneInstructions: {
    defaultMessage: 'Drag and drop files here, or click to browse',
    id: 'zNCASE4uk9',
    description: 'Instructional copy inside the create-case file dropzone',
  },
  createCaseDropzoneHint: {
    defaultMessage: 'Supported: .txt, .md, .pdf, .docx — up to 10MB each, 25MB total',
    id: '4kT5DKPt4I',
    description: 'Format and size hint inside the create-case file dropzone',
  },
  createCasePatientNameLabel: {
    defaultMessage: 'Patient name (optional)',
    id: 'BT/OFlBZbI',
    description:
      'Label for the optional patient display name field in the create-case dialog',
  },
  createCasePatientNamePlaceholder: {
    defaultMessage: 'e.g. Jane Doe',
    id: 'MCTpWh8bY4',
    description: 'Placeholder for the optional patient display name field',
  },
  createCaseDobLabel: {
    defaultMessage: 'Date of birth',
    id: '72myfsLI+o',
    description: 'Label for the required date-of-birth field in the create-case dialog',
  },
  createCaseDateAdmittedLabel: {
    defaultMessage: 'Date admitted',
    id: 'B0Hlx9d0ol',
    description: 'Label for the required date-admitted field in the create-case dialog',
  },
  createCaseLengthOfStayLabel: {
    defaultMessage: 'Length of stay in days (optional)',
    id: 'H50QRqJnNT',
    description: 'Label for the optional length-of-stay field in the create-case dialog',
  },
  createCaseLengthOfStayPlaceholder: {
    defaultMessage: 'e.g. 4',
    id: 'ppdnFFGeXQ',
    description: 'Placeholder for the optional length-of-stay field',
  },
  createCaseDischargeUnitLabel: {
    defaultMessage: 'Discharge ward or unit (optional)',
    id: 'TZkX+tR521',
    description: 'Label for the optional discharge ward field in the create-case dialog',
  },
  createCaseDischargeUnitPlaceholder: {
    defaultMessage: 'e.g. Ward 5B',
    id: '7rABZmxbm4',
    description: 'Placeholder for the optional discharge ward field',
  },
  createCaseSpecialtyLabel: {
    defaultMessage: 'Specialty',
    id: 'tSv8AgUlD+',
    description: 'Label for the required specialty field in the create-case dialog',
  },
  createCaseSpecialtyEmergencyOption: {
    defaultMessage: 'Emergency',
    id: 'QZ7rXAjRAw',
    description: 'Specialty select option for emergency in the create-case dialog',
  },
  createCaseSpecialtyRetinopathyOption: {
    defaultMessage: 'Retinopathy',
    id: 'J4QDVOCHxn',
    description: 'Specialty select option for retinopathy in the create-case dialog',
  },
  createCaseSubmit: {
    defaultMessage: 'Create case',
    id: '8EqeX7evZl',
    description: 'Submit button label in the create-case dialog',
  },
  createCaseCancel: {
    defaultMessage: 'Cancel',
    id: 'iyKRQbNBPq',
    description: 'Cancel button label in the create-case dialog',
  },
  createCaseRemoveFileAriaLabel: {
    defaultMessage: 'Remove {fileName}',
    id: 'CdzbdMVcfh',
    description:
      'Accessible label for removing a selected file in the create-case dialog',
  },
  createCaseFilesRejected: {
    defaultMessage: 'Some files were skipped — unsupported type or larger than 10MB.',
    id: 'EWkKww5TJi',
    description: 'Inline message when files are rejected by the create-case picker',
  },
  createCaseTotalTooLarge: {
    defaultMessage: 'Total upload size exceeds 25MB. Remove some files and try again.',
    id: 'CmupdM4A+N',
    description: 'Inline message when the combined upload size is too large',
  },
  createCaseSuccessTitle: {
    defaultMessage: 'Case is being created',
    id: '0iBLopNWO7',
    description: 'Success toast title after submitting a self-serve case',
  },
  createCaseSuccessDescription: {
    defaultMessage: "It'll appear in the list shortly.",
    id: '6FB9DIXNwf',
    description: 'Success toast body after submitting a self-serve case',
  },
  createCaseErrorTitle: {
    defaultMessage: 'Unable to create case',
    id: 'A1R/hKccXf',
    description: 'Error toast/inline title when self-serve case creation fails',
  },
  createCaseErrorValidation: {
    defaultMessage: 'One or more files are an unsupported type or too large.',
    id: 'y2Fz1tQM/c',
    description: 'Error message for a 422 response from the self-serve endpoint',
  },
  createCaseErrorStorage: {
    defaultMessage: 'Upload storage is not configured. Please contact an administrator.',
    id: '6ZwU7zBURK',
    description: 'Error message for a 503 response from the self-serve endpoint',
  },
  createCaseErrorConflict: {
    defaultMessage: 'A case with this id is already being processed.',
    id: 's0Z+3MY3mq',
    description: 'Error message for a 409 response from the self-serve endpoint',
  },
  createCaseErrorGeneric: {
    defaultMessage: 'Something went wrong. Please try again.',
    id: 'V8GzZQGB5l',
    description: 'Generic error message when self-serve case creation fails',
  },
  episodeColumnLabel: {
    defaultMessage: 'Episode #',
    id: '/RXBfL83Kk',
    description: 'Column header for the coding dashboard episode identifier',
  },
  patientColumnLabel: {
    defaultMessage: 'Patient',
    id: '7ng1xaLtLh',
    description: 'Column header for the coding dashboard patient name',
  },
  dateOfBirthColumnLabel: {
    defaultMessage: 'DOB',
    id: 'I/sM5TCToc',
    description: 'Column header for the coding dashboard date of birth',
  },
  primaryDiagnosisColumnLabel: {
    defaultMessage: 'Primary Dx',
    id: '2qaehK6MpI',
    description: 'Column header for the coding dashboard primary diagnosis',
  },
  dischargeUnitColumnLabel: {
    defaultMessage: 'Discharge unit',
    id: '9AEwiU2boI',
    description: 'Column header for the coding dashboard discharge unit',
  },
  dischargeWardColumnLabel: {
    defaultMessage: 'Discharge ward',
    id: 'Qx8pjAc0xe',
    description: 'Column header for the coding dashboard discharge ward',
  },
  dateDischargedColumnLabel: {
    defaultMessage: 'Date discharged',
    id: 'zhYbjUBtbG',
    description: 'Column header for the coding dashboard discharge date',
  },
  terminalDigitColumnLabel: {
    defaultMessage: 'Terminal digit',
    id: 'I8Wm2XHe7b',
    description: 'Column header for the coding dashboard terminal digit',
  },
  terminalDigitFilterPlaceholder: {
    defaultMessage: 'e.g. 2, 4, 8-15',
    id: 'vwpStiN028',
    description:
      'Placeholder for the coding dashboard terminal digit filter: single digits, comma-separated digits, or a hyphenated range',
  },
  terminalDigitFilterError: {
    defaultMessage: 'Use digits, a comma list, or a range like 2-20',
    id: 'u+N/0bLA7s',
    description:
      'Inline validation error for invalid coding dashboard terminal digit filter syntax',
  },
  divisionColumnLabel: {
    defaultMessage: 'Division',
    id: '0JC/Yp73BL',
    description: 'Column header for the coding dashboard division',
  },
  careTypeColumnLabel: {
    defaultMessage: 'Care type',
    id: '3GhzZpmXku',
    description: 'Column header for the coding dashboard care type',
  },
  dischargeCodeColumnLabel: {
    defaultMessage: 'Discharge code',
    id: 'JRzs6QUVI9',
    description: 'Column header for the coding dashboard discharge code',
  },
  lengthOfStayColumnLabel: {
    defaultMessage: 'LOS',
    id: 'WyLvOi4wEF',
    description: 'Column header for the coding dashboard length of stay in days',
  },
  selectAllRowsAriaLabel: {
    defaultMessage: 'Select all',
    id: '8aa5tCwXva',
    description: 'Accessible label for the coding dashboard table select-all checkbox',
  },
  selectRowAriaLabel: {
    defaultMessage: 'Select row',
    id: 'HFnC6Hf2QT',
    description: 'Accessible label for a coding dashboard table row selection checkbox',
  },
});
